import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationMeta } from '@/lib/api/schemas';
import { Button } from './Button';
import { Select } from './Select';

const PER_PAGE_OPTIONS = [25, 50, 100, 200]; // backend max is 200

function pageWindow(current: number, last: number): (number | '…')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages = new Set([1, last, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

/** Driven directly by the API `meta` object. */
export function Pagination({
  meta,
  onPageChange,
  onPerPageChange,
}: {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}) {
  const { current_page: page, last_page: last, total, per_page: perPage } = meta;
  const from = total === 0 ? 0 : (meta.from ?? (page - 1) * perPage + 1);
  const to = meta.to ?? Math.min(page * perPage, total);

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-sm text-fg-muted"
    >
      <p aria-live="polite">{total === 0 ? 'No results' : `Showing ${from}–${to} of ${total}`}</p>
      <div className="flex items-center gap-2">
        {onPerPageChange && (
          <Select
            size="sm"
            aria-label="Rows per page"
            value={String(perPage)}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            options={(PER_PAGE_OPTIONS.includes(perPage)
              ? PER_PAGE_OPTIONS
              : [...PER_PAGE_OPTIONS, perPage].sort((a, b) => a - b)
            ).map((n) => ({ value: n, label: `${n} / page` }))}
            className="w-32"
          />
        )}
        <Button
          variant="outline"
          size="sm"
          iconOnly
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <ul className="hidden items-center gap-1 sm:flex">
          {pageWindow(page, last).map((p, i) =>
            p === '…' ? (
              <li key={`gap-${i}`} aria-hidden className="px-1">
                …
              </li>
            ) : (
              <li key={p}>
                <Button
                  size="sm"
                  variant={p === page ? 'primary' : 'ghost'}
                  aria-label={`Page ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => onPageChange(p)}
                  className="min-w-8 px-2"
                >
                  {p}
                </Button>
              </li>
            ),
          )}
        </ul>
        <span className="sm:hidden">
          {page} / {last}
        </span>
        <Button
          variant="outline"
          size="sm"
          iconOnly
          aria-label="Next page"
          disabled={page >= last}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </nav>
  );
}
